import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/form/input';
import { Upload, Loader2, FileCheck, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { uploadActivityFile } from '@/lib/api';
import { auth } from '@/lib/auth';
import { useAuth } from '@/context/AuthContext';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { AxiosError } from 'axios';

const ALLOWED_EXTENSIONS = ['.fit', '.gpx', '.tcx'];
const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type FileType = 'FIT' | 'GPX' | 'TCX';

const getFileType = (filename: string): FileType | null => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.fit')) return 'FIT';
  if (lower.endsWith('.gpx')) return 'GPX';
  if (lower.endsWith('.tcx')) return 'TCX';
  return null;
};

const formatFileSize = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(2)} MB`;

export function ActivityUploadSection() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { status: authStatus } = useAuth();
  const isAuthenticated = authStatus === 'authenticated' && auth.isLoggedIn();

  const validateFile = (file: File): string | null => {
    const fileName = file.name.toLowerCase();
    if (!ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext))) return `Invalid file type. Only ${ALLOWED_EXTENSIONS.join(', ')} accepted.`;
    if (file.size > MAX_FILE_SIZE_BYTES) return `File too large. Maximum ${MAX_FILE_SIZE_MB}MB.`;
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = e.target.files;
    if (!files || files.length === 0) { setSelectedFile(null); return; }
    const file = files[0];
    const validationError = validateFile(file);
    if (validationError) { setError(validationError); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; return; }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !isAuthenticated || isUploading) return;
    setIsUploading(true); setError(null);
    try {
      const response = await uploadActivityFile(selectedFile);
      if (response.deduplicated) { toast({ title: 'Activity already exists' }); }
      else { toast({ title: 'Activity uploaded successfully' }); }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['activities'] }),
        queryClient.invalidateQueries({ queryKey: ['calendar'] }),
        queryClient.invalidateQueries({ queryKey: ['calendarToday'] }),
        queryClient.invalidateQueries({ queryKey: ['calendarWeek'] }),
        queryClient.invalidateQueries({ queryKey: ['calendarSeason'] }),
      ]);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      let errorMessage = 'Upload failed, please try again';
      if (error && typeof error === 'object' && 'isAxiosError' in error) {
        const axiosError = error as AxiosError<{ message?: string; detail?: string }>;
        const status = axiosError.response?.status;
        if (status === 400) errorMessage = 'Invalid file format';
        else if (status === 401) errorMessage = 'Please sign in to upload';
        else if (status === 413) errorMessage = `File too large (max ${MAX_FILE_SIZE_MB}MB)`;
        else if (status === 422) errorMessage = 'Could not parse activity file';
        else if (status === 429) errorMessage = 'Too many uploads today';
      }
      setError(errorMessage);
      toast({ title: 'Upload failed', description: errorMessage, variant: 'destructive' });
    } finally { setIsUploading(false); }
  };

  const isUploadDisabled = !selectedFile || !isAuthenticated || isUploading;

  return (
    <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
          <Upload className="h-4 w-4 text-[#00FFC8]" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-foreground">Upload Activity</h3>
          <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">FILE IMPORT</p>
        </div>
      </div>

      <div className="space-y-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".fit,.gpx,.tcx"
                  onChange={handleFileChange}
                  disabled={!isAuthenticated || isUploading}
                  className={cn(
                    'bg-white/[0.03] border-white/[0.1] font-mono text-sm min-h-[44px]',
                    (!isAuthenticated || isUploading) && 'opacity-50 cursor-not-allowed'
                  )}
                  aria-label="Select activity file (FIT, GPX, or TCX)"
                />
              </div>
            </TooltipTrigger>
            {!isAuthenticated && <TooltipContent><p>Sign in to upload activities</p></TooltipContent>}
          </Tooltip>
        </TooltipProvider>

        <p className="text-[9px] font-mono text-white/25 tracking-wider">
          [SYS] ACCEPTED FORMATS: FIT, GPX, TCX — MAX {MAX_FILE_SIZE_MB}MB
        </p>

        {selectedFile && !error && (() => {
          const fileType = getFileType(selectedFile.name);
          return (
            <div className="flex items-center gap-2 text-[10px] font-mono text-[#A0A0A0] bg-white/[0.03] border border-white/[0.06] px-3 py-2 rounded-md">
              <FileCheck className="h-3.5 w-3.5 text-[#00FFC8] shrink-0" />
              <span className="truncate">{selectedFile.name}</span>
              <span className="text-white/30">· {formatFileSize(selectedFile.size)}</span>
              {fileType && <span className="text-[#00FFC8]">· {fileType}</span>}
            </div>
          );
        })()}

        {error && (
          <div className="flex items-center gap-2 text-[10px] font-mono text-[hsl(0,70%,55%)] bg-[hsl(0,70%,55%)]/[0.06] border border-[hsl(0,70%,55%)]/20 px-3 py-2 rounded-md">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <button
                  onClick={handleUpload}
                  disabled={isUploadDisabled}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase transition-all duration-200 border min-h-[44px]',
                    !isUploadDisabled
                      ? 'border-[rgba(0,255,200,0.3)] bg-[rgba(0,255,200,0.08)] text-[#00FFC8] hover:bg-[rgba(0,255,200,0.15)] hover:shadow-[0_0_16px_rgba(0,255,200,0.15)]'
                      : 'border-white/[0.06] bg-white/[0.02] text-white/30 cursor-not-allowed'
                  )}
                  aria-label="Upload activity file"
                >
                  {isUploading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> UPLOADING...</> : <><Upload className="h-3.5 w-3.5" /> UPLOAD ACTIVITY</>}
                </button>
              </div>
            </TooltipTrigger>
            {!isAuthenticated && <TooltipContent><p>Sign in to upload activities</p></TooltipContent>}
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
